/**
 * Runtime i18n utilities for the web app.
 *
 * Executable code that depends on the browser environment
 * (localStorage, DOM) lives here — not in the pure translations library.
 */

import {
	DEFAULT_LOCALE,
	en,
	type Locale,
	type PluralForms,
	pl,
	SUPPORTED_LOCALES,
	type Translations,
	uk,
} from "@zplanem/translations"

const translations: Record<Locale, Translations> = { pl, en, uk }

const STORAGE_KEY = "zplanem_locale"

/**
 * Get translations for a given locale.
 * Falls back to Polish if locale is unknown.
 */
export function getTranslations(locale: Locale): Translations {
	return translations[locale] ?? translations[DEFAULT_LOCALE]
}

/**
 * Read the persisted locale from browser localStorage.
 */
export function getStoredLocale(): Locale {
	try {
		const stored = localStorage.getItem(STORAGE_KEY)
		if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
			return stored as Locale
		}
	} catch {
		// localStorage unavailable
	}
	return DEFAULT_LOCALE
}

/**
 * Persist the user's locale choice to browser localStorage.
 */
export function setStoredLocale(locale: Locale): void {
	try {
		localStorage.setItem(STORAGE_KEY, locale)
	} catch {
		// localStorage unavailable
	}
}

/** BCP-47 tags for `Intl` lookups; our `Locale` codes are bare languages. */
const BCP47: Record<Locale, string> = {
	pl: "pl-PL",
	en: "en-US",
	uk: "uk-UA",
}

/**
 * Resolve a count-dependent string. Polish and Ukrainian select genuinely
 * different CLDR categories for the same number (21 is `many` in Polish but
 * `one` in Ukrainian), so the category comes from `Intl.PluralRules` rather
 * than from a hand-rolled n % 10 rule.
 *
 * Falls back to `other` for any category the forms do not carry (fractions),
 * then substitutes every `{count}`.
 */
export function plural(
	forms: PluralForms,
	count: number,
	locale: Locale,
): string {
	const category = new Intl.PluralRules(
		BCP47[locale] ?? BCP47[DEFAULT_LOCALE],
	).select(count)
	const form =
		(forms as Partial<Record<Intl.LDMLPluralRule, string>>)[category] ??
		forms.other
	return form.split("{count}").join(String(count))
}
